/**
 * Centralized Auth Utility
 *
 * Single place for:
 * - Getting and validating the stored token
 * - Checking if token is expired (JWT exp)
 * - Clearing auth and redirecting to login when unauthenticated
 *
 * Use this everywhere: API interceptors, ProtectedRoute, Layout, SectionRoute.
 */

const TOKEN_KEY = "token";
const USER_ACCESS_TOKEN_KEY = "userAccessToken";
const SECTIONS_KEY = "sections";
const PERMISSIONS_KEY = "agentPermissions";
const LOGIN_PATH = "/login";

function disconnectLiveChatSocket() {
  try {
    // Lazy import avoids pulling socket.io-client into every auth check path.
    const { disconnectSocket } = require("./socket");
    disconnectSocket();
  } catch {
    /* ignore */
  }
}

/** Small buffer (seconds) before exp so we redirect just before expiry */
const EXPIRY_BUFFER_SEC = 10;

/**
 * Get the stored auth token from localStorage.
 * Checks both "token" and "userAccessToken" for compatibility.
 * @returns {string|null} Token string or null if missing/invalid
 */
export function getStoredToken() {
  let token = localStorage.getItem(TOKEN_KEY);
  if (!token || token === "null" || token === "undefined") {
    token = localStorage.getItem(USER_ACCESS_TOKEN_KEY);
  }
  if (!token || token === "null" || token === "undefined") {
    return null;
  }
  return token;
}

/**
 * Check if a JWT token is expired using its "exp" claim (seconds since epoch).
 * Uses a small buffer so we treat "about to expire" as expired.
 * @param {string} token - JWT token string
 * @returns {boolean} true if expired or invalid, false if still valid
 */
export function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp; // seconds since epoch
    if (!exp) return true;
    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec >= exp - EXPIRY_BUFFER_SEC;
  } catch {
    return true; // invalid token = treat as expired
  }
}

/**
 * Returns true if the user has a valid (present and not expired) token.
 * Use this for route protection and layout checks.
 * @returns {boolean}
 */
export function isAuthenticated() {
  const token = getStoredToken();
  return !!token && !isTokenExpired(token);
}

/**
 * Resolve the current user's role from stored auth data.
 * JWT is checked first so a fresh super-admin login is not overridden by a
 * stale `userRole` left over from an agent session on the same browser.
 * @returns {'super_admin'|'agent'|null}
 */
export function getUserRole() {
  const token = getStoredToken();
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.role === "super_admin" || payload.role === "agent") {
        return payload.role;
      }
    } catch {
      // ignore
    }
  }

  try {
    const raw = localStorage.getItem("user");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.role === "super_admin" || parsed?.role === "agent") {
        return parsed.role;
      }
    }
  } catch {
    // ignore
  }

  if (localStorage.getItem("userRole") === "agent") return "agent";
  return null;
}

/**
 * Account owner (super admin). Agents are never super admins.
 * Falls back to JWT ownership: user_id === platform_account_id.
 */
export function isSuperAdmin() {
  const role = getUserRole();
  if (role === "agent") return false;
  if (role === "super_admin") return true;

  const token = getStoredToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.role === "agent") return false;
    if (payload.role === "super_admin") return true;
    const userId = Number(payload.user_id);
    const accountId = Number(payload.platform_account_id);
    if (userId && accountId && userId === accountId) return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * Clear stored auth tokens without redirecting.
 * Use before login/signup when a stale expired token would block the request.
 */
export function clearStoredAuth() {
  disconnectLiveChatSocket();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_ACCESS_TOKEN_KEY);
  localStorage.removeItem(SECTIONS_KEY);
  localStorage.removeItem(PERMISSIONS_KEY);
  localStorage.removeItem("user");
  localStorage.removeItem("user_id");
  localStorage.removeItem("agent");
  localStorage.removeItem("agentAccessToken");
  localStorage.removeItem("userRole");
}

/**
 * Clear auth data and redirect to login.
 * Use on 401 responses or when token is expired/invalid.
 * Does nothing if already on the login page.
 */
export function clearAuthAndRedirectToLogin() {
  clearStoredAuth();

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.href = LOGIN_PATH;
  }
}

/**
 * Full logout: clear all auth-related keys and redirect to login.
 * Use when user clicks "Log out" (e.g. Sidebar).
 */
export function logout() {
  clearStoredAuth();

  if (window.location.pathname !== LOGIN_PATH) {
    window.location.href = LOGIN_PATH;
  }
}
